/**
 * Component Service
 *
 * Provides API for managing optional local AI components
 * including Ollama, Fooocus, WhisperX, and others.
 */

import { api } from './api-adapters';
import { getApiBaseUrl } from './urlResolver';

// Types

export interface GPUInfo {
  name: string | null;
  vendor: string | null;
  vramGb: number;
  cudaAvailable: boolean;
  cudaVersion: string | null;
  computeCapability: string | null;
  metalAvailable: boolean;
  rocmAvailable: boolean;
}

export interface SystemInfo {
  os: string;
  osVersion: string;
  cpuName: string;
  cpuCores: number;
  ramTotalGb: number;
  ramAvailableGb: number;
  diskTotalGb: number;
  diskAvailableGb: number;
}

export interface HardwareCapabilities {
  gpu: GPUInfo | null;
  system: SystemInfo;
  canRunOllama: boolean;
  canRunFooocus: boolean;
  canRunWhisperx: boolean;
  canRunNllb: boolean;
  canRunKokoroTts: boolean;
  canRunPiperTts: boolean;
  componentCompatibility: Record<string, boolean>;
  recommendedComponents: string[];
}

export interface ComponentRequirements {
  ram_gb?: number;
  vram_gb?: number;
  disk_gb?: number;
  gpu_required?: boolean;
}

export type ComponentStatus =
  | 'not_installed'
  | 'installing'
  | 'installed'
  | 'updating'
  | 'running'
  | 'stopped'
  | 'error';

export interface ComponentInfo {
  id: string;
  name: string;
  description: string;
  version: string | null;
  status: ComponentStatus;
  sizeMb: number;
  installedSizeMb: number | null;
  requirements: ComponentRequirements;
  dependencies: string[];
  category: string;
  icon: string;
  docsUrl: string;
  hasService: boolean;
  vramUsageMb: number;
  vramRequiredMb: number;
  isLoaded: boolean;
  idleSeconds: number | null;
}

export interface VRAMComponentInfo {
  id: string;
  vramMb: number;
  loadedAt: number;
  lastUsedAt: number;
  idleSeconds: number;
  isIdle: boolean;
  color: string;
}

export interface VRAMStatus {
  totalMb: number;
  usedMb: number;
  freeMb: number;
  headroomMb: number;
  autoUnloadEnabled: boolean;
  idleTimeoutSeconds: number;
  components: VRAMComponentInfo[];
  realUsage: { total: number; used: number; free: number };
}

export interface InstallProgress {
  step: string;
  progress: number;
  message: string;
  component?: string;
}

// API Functions

/**
 * Get hardware capabilities for the system
 */
export async function getHardwareCapabilities(): Promise<HardwareCapabilities> {
  const response = await api.get<HardwareCapabilities>('/api/v1/system/hardware');
  return response.data;
}

/**
 * List all available components and their status
 */
export async function listComponents(): Promise<Record<string, ComponentInfo>> {
  const response = await api.get<Record<string, ComponentInfo>>('/api/v1/components');
  return response.data;
}

/**
 * Get information about a specific component
 */
export async function getComponent(componentId: string): Promise<ComponentInfo> {
  const response = await api.get<ComponentInfo>(`/api/v1/components/${componentId}`);
  return response.data;
}

/**
 * Install a component
 */
export async function installComponent(
  componentId: string
): Promise<{ status: string; component: string }> {
  const response = await api.post<{ status: string; component: string }>(
    `/api/v1/components/${componentId}/install`
  );
  return response.data;
}

/**
 * Uninstall a component
 */
export async function uninstallComponent(
  componentId: string
): Promise<{ status: string; component: string }> {
  const response = await api.delete<{ status: string; component: string }>(
    `/api/v1/components/${componentId}`
  );
  return response.data;
}

/**
 * Start a component service
 */
export async function startComponent(
  componentId: string
): Promise<{ status: string; component: string }> {
  const response = await api.post<{ status: string; component: string }>(
    `/api/v1/components/${componentId}/start`
  );
  return response.data;
}

/**
 * Stop a component service
 */
export async function stopComponent(
  componentId: string
): Promise<{ status: string; component: string }> {
  const response = await api.post<{ status: string; component: string }>(
    `/api/v1/components/${componentId}/stop`
  );
  return response.data;
}

export interface InstallStatusResponse {
  step: string;
  progress: number;
  message: string;
  done: boolean;
  error: string | null;
}

/**
 * Get installation log for a component
 */
export async function getInstallLog(componentId: string): Promise<{ log: string; path: string }> {
  const response = await api.get<{ log: string; path: string }>(
    `/api/v1/components/${componentId}/install-log`
  );
  return response.data;
}

/**
 * Check installation progress via JSON (non-SSE polling fallback)
 */
export async function checkInstallStatus(componentId: string): Promise<InstallStatusResponse> {
  const response = await api.get<InstallStatusResponse>(
    `/api/v1/components/${componentId}/install-status`
  );
  return response.data;
}

/**
 * Subscribe to installation progress via SSE
 */
export function subscribeToInstallProgress(
  componentId: string,
  onProgress: (progress: InstallProgress) => void,
  onComplete: () => void,
  onError: (error: Error) => void
): () => void {
  const baseUrl = getApiBaseUrl();
  const eventSource = new EventSource(`${baseUrl}/api/v1/components/${componentId}/progress`);
  let receivedTerminal = false;
  let closed = false;

  eventSource.onmessage = (event) => {
    try {
      const progress: InstallProgress = JSON.parse(event.data);
      onProgress(progress);

      if (progress.step === 'complete' || progress.step === 'error') {
        receivedTerminal = true;
        closed = true;
        eventSource.close();
        onComplete();
      }
    } catch (e) {
      console.error('Failed to parse progress event:', e);
    }
  };

  eventSource.onerror = () => {
    // Don't fire onError if we already received a terminal event
    // (the server closes the stream after complete/error which triggers onerror)
    if (receivedTerminal || closed) {
      return;
    }
    closed = true;
    eventSource.close();

    // Poll the progress endpoint once as fallback to get the actual state
    fetch(`${baseUrl}/api/v1/components/${componentId}/progress`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.text();
      })
      .then((text) => {
        // Parse SSE format: "data: {...}\n\n"
        const match = text.match(/^data:\s*(.+)$/m);
        if (match) {
          try {
            const progress: InstallProgress = JSON.parse(match[1]);
            // Forward any valid progress to caller (including idle)
            onProgress(progress);
            if (
              progress.step === 'error' ||
              progress.step === 'complete' ||
              progress.step === 'idle'
            ) {
              onComplete();
            }
            return;
          } catch {
            // ignore parse error
          }
        }
        onError(new Error('Connection to progress stream failed'));
      })
      .catch((err) => {
        onError(new Error(`Connection to progress stream failed: ${err.message}`));
      });
  };

  // Return cleanup function
  return () => {
    closed = true;
    eventSource.close();
  };
}

// VRAM Management functions

/**
 * Get current VRAM allocation status
 */
export async function getVRAMStatus(): Promise<VRAMStatus> {
  const response = await api.get<VRAMStatus>('/api/v1/components/vram');
  return response.data;
}

/**
 * Load a component into GPU VRAM
 */
export async function loadComponent(
  componentId: string
): Promise<{ status: string; component: string }> {
  const response = await api.post<{ status: string; component: string }>(
    `/api/v1/components/${componentId}/load`
  );
  return response.data;
}

/**
 * Unload a component from GPU VRAM
 */
export async function unloadComponent(
  componentId: string
): Promise<{ status: string; component: string }> {
  const response = await api.post<{ status: string; component: string }>(
    `/api/v1/components/${componentId}/unload`
  );
  return response.data;
}

// Helper functions

/**
 * Check if a component can run on the current system.
 *
 * Uses the dynamic componentCompatibility map returned by the backend.
 * If the map is absent (schema drift / old backend) or does not contain
 * the requested component, returns false so the UI blocks installation
 * rather than silently claiming incompatible hardware is fine.
 *
 * Fail-closed: unknown component = not compatible.
 */
export function canRunComponent(hardware: HardwareCapabilities, componentId: string): boolean {
  if (hardware.componentCompatibility && componentId in hardware.componentCompatibility) {
    return hardware.componentCompatibility[componentId];
  }

  // componentCompatibility map is missing or does not know this component.
  // Fail-closed: do not assume compatibility when we have no evidence for it.
  return false;
}

/**
 * Get icon for a component
 */
export function getComponentIcon(componentId: string): string {
  const icons: Record<string, string> = {
    ollama: 'brain',
    fooocus: 'image',
    whisperx: 'mic',
    nllb: 'languages',
    kokoro_tts: 'volume-2',
    piper_tts: 'volume-1',
  };
  return icons[componentId] || 'package';
}

/**
 * Format size in MB/GB
 */
export function formatSize(sizeMb: number): string {
  if (sizeMb >= 1000) {
    return `${(sizeMb / 1000).toFixed(1)} GB`;
  }
  return `${sizeMb} MB`;
}

/**
 * Get status color class
 */
export function getStatusColor(status: ComponentStatus): string {
  switch (status) {
    case 'installed':
    case 'running':
      return 'text-green-600 dark:text-green-400';
    case 'installing':
    case 'updating':
      return 'text-blue-600 dark:text-blue-400';
    case 'stopped':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'error':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-secondary-600 dark:text-secondary-400';
  }
}

/**
 * Get status badge variant
 */
export function getStatusBadge(status: ComponentStatus): { text: string; variant: string } {
  switch (status) {
    case 'installed':
      return { text: 'Installed', variant: 'success' };
    case 'running':
      return { text: 'Running', variant: 'success' };
    case 'installing':
      return { text: 'Installing...', variant: 'info' };
    case 'updating':
      return { text: 'Updating...', variant: 'info' };
    case 'stopped':
      return { text: 'Stopped', variant: 'warning' };
    case 'error':
      return { text: 'Error', variant: 'error' };
    default:
      return { text: 'Not Installed', variant: 'default' };
  }
}
